import React, { useMemo } from "react";

import { ColDef } from "ag-grid-community";
import { get } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { updateDeploymentAttribute, updateDeploymentAttributes } from "actions";

import { ConfirmDialog, createModalTitle } from "components/ConfirmDialog";

import { UPDATE_DEPLOYMENT_LABELS } from "constants/actionTypes";
import { ACTION_COLUMN_NAME } from "constants/aggrid";

import ExtendableTable from "containers/Settings/ExtendableTable";
import { HeaderBulkSelectCellRenderer } from "containers/Settings/Renderers";

import { isAgriNousManagedGetter } from "lib/agGrid/getters";
import { AgriNousManagedRenderer } from "lib/agGrid/renderers";

import { getActiveDeploymentAttributes, getIsLivestockAgent } from "selectors";

import { useHasBillingPermission } from "hooks";

import { labelActionsColumnRenderer } from "./actions";

function getIsNameEditable({ data }) {
  // Name is editable if it's not a agrinous managed label.
  return !get(data, "master_label_id");
}

// `DeploymentLabel` (state.d.ts) matches the row shape used by these
// column defs (master_label_id, name, quick_select, is_manual_charge_label).
const baseColumnDefs = [
  {
    headerName: "AgriNous Managed?",
    field: "master_label_id",
    rowDrag: true,
    valueGetter: isAgriNousManagedGetter,
    cellRenderer: AgriNousManagedRenderer,
    editable: false,
  },

  { headerName: "Name", field: "name", editable: getIsNameEditable },
  {
    headerName: "Sale Lot",
    field: "quick_select",
    cellRenderer: "checkboxRenderer",
    editable: false,
    headerComponent: HeaderBulkSelectCellRenderer,
  },
  {
    headerName: "Sundries",
    field: "is_manual_charge_label",
    cellRenderer: "checkboxRenderer",
    editable: false,
    headerComponent: HeaderBulkSelectCellRenderer,
  },
  {
    headerName: ACTION_COLUMN_NAME,
    cellRenderer: labelActionsColumnRenderer,
    editable: false,
    valueGetter: () => null,
  },
] as const satisfies readonly ColDef<DeploymentLabel>[];

const validationSchema = Yup.object().shape({
  name: Yup.string().required(),
});

const blankRowDefaults = { name: "", is_used: true, quick_select: true };

export const LabelSettings = () => {
  const [deleteLabel, setDeleteLabel] = React.useState(null);
  // `getActiveDeploymentAttributes` only knows every collection as the shared
  // `DeploymentAttribute` base - narrowed here to what this page actually gets.
  const deploymentData = useSelector(getActiveDeploymentAttributes)
    .labels as DeploymentLabel[];
  const dispatch = useDispatch();

  const isLivestockAgent = useSelector(getIsLivestockAgent);

  const hasBillingPermission = useHasBillingPermission();

  const showBillingColumn = hasBillingPermission && isLivestockAgent;

  const columnDefs = useMemo<ColDef<DeploymentLabel>[]>(
    () =>
      baseColumnDefs.filter(
        (column: ColDef<DeploymentLabel>) =>
          showBillingColumn || column.field !== "is_manual_charge_label",
      ),
    [showBillingColumn],
  );

  const handleOpenDeleteDialog = label => setDeleteLabel(label);

  const handleCloseDeleteDialog = () => setDeleteLabel(null);

  const handleDeleteLabel = () => {
    const labelPayload = {
      ...deleteLabel,
      deprecated: true,
    };
    dispatch(updateDeploymentAttribute(UPDATE_DEPLOYMENT_LABELS, labelPayload));
  };

  const onDelete = () => {
    handleDeleteLabel();
    handleCloseDeleteDialog();
  };

  const handleUpdate = (payload: DeploymentLabel[]) => {
    const labelsPayload = payload.map(p => ({
      id: p.id,
      order: p.order,
      deployment_id: p.deployment_id,
      species_id: p.species_id,
      is_used: p.is_used || false,
      // DeploymentLabel has no `code` field on the backend (confirmed against
      // the serializer) - this has always sent `undefined`, which the API
      // silently ignores. Left as-is; only widened to compile.
      code: (p as DeploymentLabel & { code?: string }).code,
      name: p.name,
      quick_select: p.quick_select || false,
      // Don't touch this if we don't have the billing permission (eg a field agent vs office staff)
      is_manual_charge_label: showBillingColumn
        ? p.is_manual_charge_label || false
        : undefined,

      deprecated: false,
    }));

    dispatch(
      updateDeploymentAttributes(UPDATE_DEPLOYMENT_LABELS, labelsPayload),
    );
  };

  return (
    <>
      <ExtendableTable
        deploymentData={deploymentData}
        handleUpdate={handleUpdate}
        columnDefs={columnDefs}
        context={{ handleOpenDeleteDialog }}
        validationSchema={validationSchema}
        blankRowDefaults={blankRowDefaults}
      />
      <ConfirmDialog
        title={createModalTitle("this label")}
        isOpen={!!deleteLabel}
        onCancel={handleCloseDeleteDialog}
        onDelete={onDelete}
      />
    </>
  );
};
